'use client';

import { W, FONT } from './tokens';
import { anim } from './motion';

/**
 * The seven overlay animations and the six closed-card motion bands
 * (design_with_animation/ANIMATIONS_AND_MODALS.md, parts 5 and 6).
 *
 * Plain divs, spans and inline SVG. No animation library, no canvas, no images,
 * exactly as specified: every one of these is a handful of elements with a CSS
 * keyframe and a stagger delay, and pulling in a library to do that would be
 * more code, not less.
 *
 * Each animation is a small true story about the product. The check-in counter
 * climbs to 1,189 of 1,432; the split ring closes on 4 of 4; the basket resolves
 * to £77. The figures are invented but the behaviour is real, and none of them
 * claim anything the software does not do.
 *
 * All of it is gated by useScrollGatedMotion: one pass on entry, rewound on
 * exit. Nothing here loops.
 */

const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#5A7871',
  paddingBottom: 16,
};

function Panel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fvw-anim" data-fv-motion style={{ fontFamily: FONT, color: W.ink }}>
      <div style={LABEL}>{label}</div>
      {children}
    </div>
  );
}

/* ── 5.1 Waves: a heat list seeding ─────────────────────────────────────── */

const HEATS: Array<[string, string]> = [
  ['09:00', 'Wave 1'],
  ['09:12', 'Wave 2'],
  ['09:24', 'Wave 3'],
  ['09:36', 'Wave 4'],
];

export function WavesAnim() {
  return (
    <Panel label="Heats built from the entries you hold">
      <div className="fvw-anim-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 22 }}>
        <div style={{ display: 'grid', gap: 7, alignContent: 'start' }}>
          {HEATS.map(([time, wave], k) => (
            <div
              key={wave}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#FFFFFF',
                border: '1px solid #D8E6E1',
                borderRadius: 8,
                padding: '9px 12px',
                animation: anim('fvSeed', k * 0.24),
              }}
            >
              <span style={{ width: 46, flex: 'none', fontSize: 14, fontWeight: 600, color: W.teal }}>{time}</span>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{wave}</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/* ── 5.2 Comms: one send fanning out to five athletes ──────────────────── */

export function CommsAnim() {
  return (
    <Panel label="One send, straight to the right athletes">
      <div className="fvw-anim-row" style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
        <span
          style={{
            width: 54, height: 54, flex: 'none', borderRadius: 12, background: W.obsidian,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={W.ice} strokeWidth={1.6} aria-hidden>
            <path d="M3 6h18v12H3z" />
            <path d="M3 6l9 7 9-7" />
          </svg>
        </span>

        <div style={{ display: 'grid', gap: 7, flex: 1, minWidth: 0 }}>
          {[0, 1, 2, 3, 4].map((k) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, animation: anim('fvPaid', k * 0.2) }}>
              <span style={{ width: 22, height: 22, flex: 'none', borderRadius: '50%', background: '#E9F4F1' }} />
              <span style={{ flex: 1, minWidth: 0, height: 6, borderRadius: 3, background: '#D8E6E1' }} />
              <span
                style={{
                  flex: 'none', fontSize: 12, fontWeight: 600, color: W.teal,
                  animation: anim('fvOpened', k * 0.2),
                }}
              >
                {k % 2 === 0 ? 'Sent' : 'Opened'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/* ── 5.3 Split payment: a ring closing on 4 of 4 ───────────────────────── */

const SHARES = ['You', 'Aoife', 'Cian', 'Mark'];

export function SplitAnim() {
  return (
    <Panel label="One captain books, everyone pays their share">
      <div className="fvw-anim-row" style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
        <span style={{ position: 'relative', width: 88, height: 88, flex: 'none' }}>
          <svg width={88} height={88} viewBox="0 0 64 64" aria-hidden>
            <circle cx="32" cy="32" r="28" fill="none" stroke="#D8E6E1" strokeWidth="5" />
            <circle
              cx="32" cy="32" r="28" fill="none" stroke={W.teal} strokeWidth="5"
              strokeLinecap="round" strokeDasharray="176" transform="rotate(-90 32 32)"
              style={{ animation: anim('fvRing') }}
            />
          </svg>
          <span
            style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 700, animation: anim('fvTick'),
            }}
          >
            4 / 4
          </span>
        </span>

        <div style={{ display: 'grid', gap: 8, flex: 1, minWidth: 0 }}>
          {SHARES.map((name, k) => (
            <div
              key={name}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 10, animation: anim('fvPaid', k * 0.42) }}
            >
              <span style={{ fontSize: 15, fontWeight: 500 }}>{name}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: W.teal }}>£45 paid</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/* ── Branded elements: what else carries the organiser's brand ─────────── */

/**
 * The brand card's overlay used to show the same three phones again, which is
 * the one thing the design brief is emphatic about not doing. The claim is that
 * the wallet pass, the standings and the emails carry the brand too, so this
 * shows those three, in the organiser's colour.
 *
 * The organiser here is INVENTED. It used est.28's name and gold, and borrowing
 * a real customer's identity for a generic illustration reads as though they
 * are the subject of the claim rather than one example of it. A purple that
 * belongs to nobody also makes the point better: the colour is arbitrary, which
 * is exactly what "your brand" means.
 */
const BRAND_ACCENT = '#7C4DFF';
const BRAND_NAME = 'HARBOUR RACING';
const BRAND_EVENT = 'Harbour Winter Series';

/** A QR block. Deterministic, so it does not change between renders. */
function QrGlyph({ size, colour = '#0C1312' }: { size: number; colour?: string }) {
  const cells: React.ReactNode[] = [];
  for (let y = 0; y < 9; y += 1) {
    for (let x = 0; x < 9; x += 1) {
      // A fixed pattern rather than randomness: Math.random() would produce a
      // different code on the server and the client and trip hydration.
      const on =
        (x * 7 + y * 13 + x * y) % 3 === 0 ||
        (x < 3 && y < 3) ||
        (x > 5 && y < 3) ||
        (x < 3 && y > 5);
      if (on) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={colour} />);
    }
  }
  return (
    <svg width={size} height={size} viewBox="0 0 9 9" aria-hidden>
      {cells}
    </svg>
  );
}

export function BrandedElementsAnim() {
  return (
    <Panel label="Your logo, your colours, your fonts, everywhere an athlete looks">
      <div className="fvw-anim-three" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
        {/* Wallet pass */}
        <Named label="Their wallet pass">
        <div
          style={{
            borderRadius: 10, overflow: 'hidden', border: '1px solid #D8E6E1', background: '#141F33',
            animation: 'fvLine 620ms cubic-bezier(0.22,1,0.36,1) 0s both',
          }}
        >
          <div style={{ padding: '9px 10px 8px' }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: BRAND_ACCENT }}>{BRAND_NAME}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F7F5F2', marginTop: 4, lineHeight: 1.15 }}>
              {BRAND_EVENT}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              {[['WAVE', '3'], ['TIME', '09:24'], ['BIB', '184']].map(([k, v]) => (
                <span key={k}>
                  <span style={{ display: 'block', fontSize: 6, letterSpacing: '0.08em', color: 'rgba(247,245,242,0.5)' }}>{k}</span>
                  <span style={{ display: 'block', fontSize: 9, fontWeight: 600, color: '#F7F5F2' }}>{v}</span>
                </span>
              ))}
            </div>
          </div>
          <div style={{ background: '#FFFFFF', padding: '8px 0', display: 'flex', justifyContent: 'center' }}>
            <QrGlyph size={38} />
          </div>

        </div>

        </Named>

        {/* Live standings */}
        <Named label="Their live standings">
        <div
          style={{
            borderRadius: 10, overflow: 'hidden', border: '1px solid #D8E6E1', background: '#FFFFFF',
            animation: 'fvLine 620ms cubic-bezier(0.22,1,0.36,1) 0.14s both',
          }}
        >
          <div style={{ background: BRAND_ACCENT, padding: '6px 9px', fontSize: 8, fontWeight: 700, color: '#FFFFFF' }}>
            {BRAND_NAME} LIVE
          </div>
          <div style={{ padding: '7px 9px 9px' }}>
            {[['1', 'Byrne / Kelly', '58:12'], ['2', 'Doyle / Fox', '59:40'], ['3', 'Nolan / Ryan', '61:05']].map(([pos, name, t], k) => (
              <div
                key={name}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0',
                  borderBottom: k === 2 ? 'none' : '1px solid #EEF2F1',
                  animation: anim('fvPaid', 0.3 + k * 0.22),
                }}
              >
                <span style={{ fontSize: 9, fontWeight: 700, color: BRAND_ACCENT, width: 8 }}>{pos}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 9, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </span>
                <span style={{ fontSize: 8.5, color: '#5A7871' }}>{t}</span>
              </div>
            ))}
          </div>

        </div>

        </Named>

        {/* Confirmation email */}
        <Named label="Their confirmation email">
        <div
          style={{
            borderRadius: 10, overflow: 'hidden', border: '1px solid #D8E6E1', background: '#FFFFFF',
            animation: 'fvLine 620ms cubic-bezier(0.22,1,0.36,1) 0.28s both',
          }}
        >
          <div style={{ padding: '8px 9px', borderBottom: '1px solid #EEF2F1' }}>
            <div style={{ fontSize: 7, color: '#93A8A2' }}>From</div>
            <div style={{ fontSize: 8, fontWeight: 600 }}>Harbour Racing</div>
          </div>
          <div style={{ padding: '8px 9px 9px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, marginBottom: 4 }}>You are in.</div>
            <div style={{ fontSize: 8, color: '#546C66', lineHeight: 1.5, marginBottom: 7 }}>
              Wave 3 at 09:24. Your pass is attached.
            </div>
            <span style={{ display: 'inline-block', fontSize: 8, fontWeight: 700, color: '#FFFFFF', background: BRAND_ACCENT, padding: '5px 9px', borderRadius: 5 }}>
              View your entry
            </span>
          </div>

        </div>
        </Named>
      </div>
    </Panel>
  );
}

/**
 * A heading above each artefact. The names used to sit underneath in 7px grey,
 * which is not a label anybody reads: three coloured rectangles with no idea
 * what any of them were.
 */
function Named({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: '#5A7871', marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

/* ── Check-in, rebuilt: a pass scanned, a row turning green ────────────── */

/**
 * The old version was four rows fading in beside a counter, which showed the
 * OUTCOME of check-in without ever showing check-in happening. This shows the
 * act: a wallet pass under a scanner, the beam crossing it, and the athlete's
 * row flipping to checked in as the counter moves.
 */
export function CheckInAnim() {
  return (
    <Panel label="A pass scanned at the gate, and the athlete is in">
      <div className="fvw-anim-row" style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
        <div
          style={{
            position: 'relative', width: 100, flex: 'none', borderRadius: 10, overflow: 'hidden',
            background: '#141F33', padding: '10px 9px 9px',
          }}
        >
          <div style={{ fontSize: 7, fontWeight: 700, letterSpacing: '0.09em', color: '#8FA8A2' }}>WAVE 3 · BIB 184</div>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: '#F7F5F2', margin: '3px 0 7px' }}>Sam Doyle</div>
          <div style={{ background: '#FFFFFF', borderRadius: 6, padding: 6, display: 'flex', justifyContent: 'center' }}>
            <QrGlyph size={46} />
          </div>
          {/* The scanner beam crossing the pass. */}
          <span
            aria-hidden
            style={{
              position: 'absolute', left: 0, right: 0, top: 0, height: 2,
              background: 'linear-gradient(90deg, rgba(42,223,223,0) 0%, #2ADFDF 50%, rgba(42,223,223,0) 100%)',
              boxShadow: '0 0 10px rgba(42,223,223,0.8)',
              animation: 'fvScan 2.4s ease-in-out both',
            }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, paddingBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', animation: anim('fvCount', 1.1) }}>
              1,189
            </span>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#5A7871' }}>of 1,432 checked in</span>
          </div>

          <div style={{ display: 'grid', gap: 7 }}>
            {([
              ['Sam Doyle', '184', true],
              ['Aoife Connolly', '221', false],
              ['Marcus Reid', '309', false],
            ] as Array<[string, string, boolean]>).map(([name, bib, scanned], k) => (
              <div
                key={name}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                  background: '#FFFFFF', borderRadius: 8, padding: '9px 13px', border: '1px solid #D8E6E1',
                  animation: scanned ? 'fvCheck 2.4s ease-out 0.9s both' : anim('fvPaid', 0.2 + k * 0.3),
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 500 }}>{name}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: scanned ? '#1F6B54' : '#93A8A2' }}>
                  {scanned ? '✓ Checked in' : `Bib ${bib}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
}

/* ── Who enters: solo, pairs, relays, clubs, corporates ────────────────── */

/**
 * The entries card's overlay used to carry a screenshot of one club's page,
 * which showed one of the five things the card claims. This shows all five
 * arriving into the same checkout, which is the actual argument.
 */
const ENTRANTS: Array<[string, string, number]> = [
  ['Solo athlete', '1 entry', 1],
  ['Pair', '2 entries, split between them', 2],
  ['Relay team', '4 entries, one captain', 4],
  ['Club', '18 members, paying individually', 6],
  ['Corporate group', '32 places, one invoice', 8],
];

export function EntrantsAnim() {
  return (
    <Panel label="Everybody through the same checkout">
      <div style={{ display: 'grid', gap: 8 }}>
        {ENTRANTS.map(([label, note, count], k) => (
          <div
            key={label}
            style={{
              display: 'flex', alignItems: 'center', gap: 11,
              background: '#FFFFFF', border: '1px solid #D8E6E1', borderRadius: 9, padding: '9px 12px',
              animation: anim('fvSeed', k * 0.26),
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', flex: 'none', width: 52 }}>
              {Array.from({ length: Math.min(count, 4) }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#E9F4F1',
                    border: '1.5px solid #FFFFFF', marginLeft: i === 0 ? 0 : -6,
                  }}
                />
              ))}
              {count > 4 && (
                <span style={{ fontSize: 9, fontWeight: 600, color: '#5A7871', marginLeft: 3 }}>+{count - 4}</span>
              )}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{label}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: '#5A7871' }}>{note}</span>
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: W.teal, animation: anim('fvOpened', k * 0.26) }}>
              In
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}


/* ── 5.5 Basket: line items dropping in, total resolving ───────────────── */

const BASKET: Array<[string, string]> = [
  ['Entry', '£38'],
  ['Event photos', '£12'],
  ['Event t-shirt', '£22'],
  ['Car parking', '£5'],
];

export function BasketAnim() {
  return (
    <Panel label="Add-ons going in with the entry">
      {BASKET.map(([item, price], k) => (
        <div
          key={item}
          style={{
            display: 'flex', justifyContent: 'space-between', gap: 10,
            borderBottom: '1px solid #D8E6E1', padding: '9px 0',
            animation: anim('fvLine', k * 0.34),
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 500 }}>{item}</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#5A7871' }}>{price}</span>
        </div>
      ))}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10,
          paddingTop: 14, animation: anim('fvTick'),
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>Total</span>
        <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em', color: W.teal }}>£77</span>
      </div>
    </Panel>
  );
}

/* ── 5.7 Payout: coins travelling from Fitvent to your own account ─────── */

export function PayoutAnim() {
  return (
    <Panel label="Money leaving Fitvent and landing in your own account">
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 78 }}>
        <span
          style={{
            width: 54, height: 54, flex: 'none', borderRadius: '50%', background: W.obsidian,
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
          }}
        >
          <svg width={22} height={22} viewBox="0 0 100 100" aria-hidden>
            <polygon points="8,14 92,14 92,28 22,28" fill={W.ice} />
            <polygon points="34,44 92,44 92,58 48,58" fill="#F4FAF8" />
            <polygon points="68,74 92,74 92,88 78,88" fill="#F4FAF8" />
          </svg>
        </span>

        {/* The coins ride the line between the two disc centres. No rail: an
            earlier version had a dashed one and it read as decoration. */}
        <span aria-hidden style={{ position: 'absolute', left: 54, right: 54, top: '50%', height: 0 }}>
          {[0, 0.5, 1].map((delay) => (
            <span
              key={delay}
              style={{
                position: 'absolute',
                top: -9,
                left: 6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#E9F4F1',
                border: `2px solid ${W.teal}`,
                animation: `fvCoin 2.4s cubic-bezier(0.4,0,0.2,1) ${delay}s both`,
              }}
            />
          ))}
        </span>

        <span
          style={{
            width: 54, height: 54, flex: 'none', borderRadius: '50%', background: '#E9F4F1',
            border: `1px solid ${W.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1, animation: 'fvLand 2.4s ease-out 1s both',
          }}
        >
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={W.teal} strokeWidth={1.7} aria-hidden>
            <path d="M3 10l9-6 9 6" />
            <path d="M5 10v8M12 10v8M19 10v8" />
            <path d="M3 20h18" />
          </svg>
        </span>
      </div>
    </Panel>
  );
}

export const ANIMS = {
  waves: WavesAnim,
  branded: BrandedElementsAnim,
  entrants: EntrantsAnim,
  comms: CommsAnim,
  split: SplitAnim,
  checkin: CheckInAnim,
  basket: BasketAnim,
  payout: PayoutAnim,
} as const;

export type AnimId = keyof typeof ANIMS;

/* ── Part 6: the closed-card motion bands ──────────────────────────────── */

const BAND_LABEL: React.CSSProperties = { fontSize: 15, fontWeight: 400, color: '#5A7871', whiteSpace: 'nowrap' };

function BandPill({ children, delay }: { children: React.ReactNode; delay: number }) {
  return (
    <span
      style={{
        fontSize: 14, fontWeight: 600, color: W.teal, background: '#E9F4F1',
        borderRadius: 6, padding: '6px 11px', whiteSpace: 'nowrap',
        animation: anim('fvSeed', delay),
      }}
    >
      {children}
    </span>
  );
}

function BandDot({ delay }: { delay: number }) {
  return (
    <span
      aria-hidden
      style={{ width: 9, height: 9, flex: 'none', borderRadius: '50%', background: W.teal, animation: anim('fvPaid', delay) }}
    />
  );
}

function BandValue({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: W.teal, animation: anim('fvTick') }}>
      {children}
    </span>
  );
}

/**
 * The live strip on a closed card. Six of the eight carry one; card 1 runs the
 * brand swap instead, which is handled inside its surface.
 *
 * These are what make the grid feel alive before anything is clicked, and they
 * are deliberately data rather than decoration: a place being held, a draw
 * being set, a counter climbing.
 */
export function MotionBand({ id }: { id: BandId }) {
  const content = {
    entries: (
      <>
        <span style={BAND_LABEL}>Team of four</span>
        {[0, 0.34, 0.68, 1.02].map((d) => (
          <BandDot key={d} delay={d} />
        ))}
        <BandValue>Place held</BandValue>
      </>
    ),
    waves: (
      <>
        <span style={BAND_LABEL}>Seeding</span>
        <BandPill delay={0}>09:00</BandPill>
        <BandPill delay={0.22}>09:12</BandPill>
        <BandPill delay={0.44}>09:24</BandPill>
        <BandPill delay={0.66}>Heats set</BandPill>
      </>
    ),
    checkin: (
      <>
        <BandDot delay={0} />
        <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', animation: anim('fvCount') }}>1,189</span>
        <span style={BAND_LABEL}>checked in of 1,432</span>
      </>
    ),
    basket: (
      <>
        <span style={BAND_LABEL}>Basket</span>
        <BandPill delay={0}>Entry</BandPill>
        <BandPill delay={0.24}>Photos</BandPill>
        <BandPill delay={0.48}>Merch</BandPill>
        <BandValue>£77</BandValue>
      </>
    ),
    comms: (
      <>
        <span style={BAND_LABEL}>Wave times sent</span>
        {[0, 0.2, 0.4, 0.6, 0.8].map((d) => (
          <BandDot key={d} delay={d} />
        ))}
        <BandValue>1,432 opened</BandValue>
      </>
    ),
    payout: (
      <>
        <span style={BAND_LABEL}>Paid out today</span>
        <CoinLane />
        <BandValue>£3,062</BandValue>
      </>
    ),
  }[id];

  return (
    <div className="fvw-band" data-fv-motion>
      {content}
    </div>
  );
}

export type BandId = 'entries' | 'waves' | 'checkin' | 'basket' | 'comms' | 'payout';

/** The Fitvent mark, a coin crossing, and the organiser's own bank. */
function CoinLane() {
  return (
    <span
      data-fv-loop
      style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, width: 132, flex: 'none' }}
    >
      <span
        aria-hidden
        style={{
          width: 22, height: 22, flex: 'none', borderRadius: '50%', background: W.obsidian,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg width={10} height={10} viewBox="0 0 100 100">
          <polygon points="8,14 92,14 92,28 22,28" fill={W.ice} />
          <polygon points="34,44 92,44 92,58 48,58" fill="#F4FAF8" />
          <polygon points="68,74 92,74 92,88 78,88" fill="#F4FAF8" />
        </svg>
      </span>

      <span aria-hidden style={{ position: 'relative', flex: 1, height: 22 }}>
        {[0].map((delay) => (
          <span
            key={delay}
            style={{
              position: 'absolute', top: 6, left: 0, width: 10, height: 10, borderRadius: '50%',
              background: '#E9F4F1', border: `1.5px solid ${W.teal}`,
              // The lane is ~52px wide here, so the travel is set explicitly
              // rather than inherited from the overlay animation's default.
              ['--fv-coin-travel' as string]: '62px',
              animation: `fvCoin 2s cubic-bezier(0.45,0,0.2,1) ${delay}s infinite`,
            }}
          />
        ))}
      </span>

      <span
        aria-hidden
        style={{
          width: 22, height: 22, flex: 'none', borderRadius: '50%', background: '#E9F4F1',
          border: `1px solid ${W.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fvLand 2s ease-out 0s infinite',
        }}
      >
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={W.teal} strokeWidth={1.8}>
          <path d="M3 10l9-6 9 6" />
          <path d="M5 10v8M12 10v8M19 10v8" />
          <path d="M3 20h18" />
        </svg>
      </span>
    </span>
  );
}

